import React, { useEffect, lazy, Suspense } from 'react';
import { IntlProvider } from 'react-intl';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import qs from 'qs';
import './app.css';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import { Box } from '@mui/material';
// eslint-disable-next-line import/no-unresolved
import locales from '@shared/locales';
// eslint-disable-next-line import/no-unresolved
import Loading from '@shared/components/loading';
// eslint-disable-next-line import/no-unresolved
import Stepper from '@shared/components/stepper';
// eslint-disable-next-line import/no-unresolved
import { useAppActions, useAppState } from '@shared/app/context';

const OnboardingRouter = lazy(() => import(/* webpackChunkName: "OnboardingRouter" */ './screens/onboarding'));
const PaymentScreen = lazy(() => import(/* webpackChunkName: "PaymentScreen" */ './screens/payment/index'));
const RedirectPaymentScreen = lazy(() =>
    import(/* webpackChunkName: "RedirectPaymentScreen" */ './screens/payment/RedirectPayment')
);
const PersonalInformationScreen = lazy(() =>
    // eslint-disable-next-line import/no-unresolved
    import(/* webpackChunkName: "PersonalInformationScreen" */ '@shared/personal_information/ui')
);
// eslint-disable-next-line import/no-unresolved
const Documents = lazy(() => import(/* webpackChunkName: "Documents" */ '@shared/documents-veriff/ui'));

const RedirectVeriff = lazy(() =>
    // eslint-disable-next-line import/no-unresolved
    import(/* webpackChunkName: "Documents" */ '@shared/documents-veriff/ui/RedirectVeriff')
);
const EndScreen = lazy(() => import(/* webpackChunkName: "EndScreen" */ './screens/end'));
const FiscalCodeScreen = lazy(() =>
    // eslint-disable-next-line import/no-unresolved
    import(/* webpackChunkName: "FiscalCode" */ '@shared/fiscal_code/ui')
);

export const PrivateRoute = (props) => {
    const { isAuthenticate } = useAppState();
    return isAuthenticate ? <Route {...props} /> : <Redirect to="/email" />;
};

Bugsnag.start({
    apiKey: '72b575900571fab0fcc06de0f199993a',
    plugins: [new BugsnagPluginReact()],
});

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const App = () => {
    const { loading, isInApp, locale, hasStepper, step } = useAppState();
    const { load, setIsInApp, setLocale, setRedirect, setLightRegistrationInformations } = useAppActions();
    const { search } = useLocation();

    const startApp = async () => {
        if (window.flutter_inappwebview && !window.flutter_inappwebview.callHandler) {
            await new Promise((resolve) => {
                setTimeout(() => resolve(), 1000);
            });
        }
        if (window.flutter_inappwebview && window.flutter_inappwebview.callHandler) {
            const langParam = await window.flutter_inappwebview.callHandler('getLocaleName');
            const refreshToken = await window.flutter_inappwebview.callHandler('getRefreshToken');
            const availableLanguages = Object.keys(locales);
            const selectedLang = availableLanguages.find(
                (lang) =>
                    lang === langParam ||
                    lang.split('_')[0] === langParam ||
                    lang.split('_')[0] === langParam.split('_')[0] ||
                    lang.split('_')[0] === langParam.split('-')[0]
            );
            if (selectedLang) {
                setLocale(selectedLang);
            }
            setIsInApp(true);
            load(refreshToken);
        } else if (search) {
            const { apps, token, lang, cityId, light, full, returnTo, refreshToken } = qs.parse(
                search.replace('?', '')
            );
            const t = token || refreshToken;

            if (apps) {
                const isInAppParam = !!apps;
                if (isInAppParam !== isInApp) {
                    setIsInApp(isInAppParam);
                }
            }
            if (lang && Object.keys(locales).find((l) => lang === l)) {
                setLocale(lang);
            }

            if (returnTo) {
                sessionStorage.setItem('returnTo', returnTo);
            }

            setLightRegistrationInformations({ cityId, isLight: light });

            if ((light || full) && cityId && lang && !t) {
                setRedirect(`/onboarding/rules_and_agreements${search}`);
            }

            load(t);
        } else {
            load();
        }
    };

    useEffect(() => {
        startApp();
    }, []);

    if (loading) {
        return <Loading />;
    }

    return (
        <ErrorBoundary>
            <IntlProvider messages={locales[locale]} locale={locale.replace('_', '-')} defaultLocale="en">
                <Box
                    sx={{
                        display: 'grid',
                        position: 'relative',
                        maxWidth: 444,
                        mx: 'auto',
                        pb: 'env(safe-area-inset-bottom)',
                        overflow: 'auto',
                    }}
                >
                    <Suspense fallback={<Loading />}>
                        <Switch>
                            <Redirect exact from="/" to="/onboarding/start" />
                            <Route path="/onboarding" component={OnboardingRouter} />
                            <PrivateRoute exact path="/redirect-veriff" component={RedirectVeriff} />
                            <PrivateRoute exact path="/documents/:id" component={Documents} />
                            <PrivateRoute exact path="/payment" component={PaymentScreen} />
                            <PrivateRoute exact path="/redirect-payment" component={RedirectPaymentScreen} />
                            <PrivateRoute exact path="/personal-information" component={PersonalInformationScreen} />
                            <PrivateRoute exact path="/fiscal-code" component={FiscalCodeScreen} />
                            <PrivateRoute exact path="/end" component={EndScreen} />
                            <Redirect to="/onboarding/start" />
                        </Switch>
                    </Suspense>
                    {hasStepper && <Stepper step={step} />}
                </Box>
            </IntlProvider>
        </ErrorBoundary>
    );
};

export default App;
